import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase-init';
import { startProduction as startProductionService } from '../services/shop-service';

const ShopContext = createContext();

export function useShop() {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within ShopProvider');
  }
  return context;
}

export function ShopProvider({ children }) {
  const [queueJobs, setQueueJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [archiveJobs, setArchiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Real-time listener for queue jobs (awaiting_production)
  useEffect(() => {
    const jobsRef = collection(db, 'jobs');
    const queueQuery = query(jobsRef, where('status', '==', 'awaiting_production'));

    const unsubscribe = onSnapshot(queueQuery, (snapshot) => {
      const jobs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(j => j.cutlistJobId);

      jobs.sort((a, b) => {
        if (a.priority === 'high' && b.priority !== 'high') return -1;
        if (a.priority !== 'high' && b.priority === 'high') return 1;
        const dateA = a.soldDate?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.soldDate?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
        return dateA - dateB;
      });

      setQueueJobs(jobs);
    }, (error) => {
      console.error('Queue listener error:', error);
    });

    return () => unsubscribe();
  }, []);

  // Real-time listener for active jobs (in_production)
  useEffect(() => {
    const jobsRef = collection(db, 'jobs');
    const activeQuery = query(jobsRef, where('status', '==', 'in_production'));

    const unsubscribe = onSnapshot(activeQuery, (snapshot) => {
      const jobs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(j => j.cutlistJobId);

      jobs.sort((a, b) => {
        const dateA = a.productionStartedAt?.toDate?.() || new Date(0);
        const dateB = b.productionStartedAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setActiveJobs(jobs);
      setLoading(false);
    }, (error) => {
      console.error('Active listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Real-time listener for archive jobs (completed, ready_to_install, scheduled, installed)
  useEffect(() => {
    const jobsRef = collection(db, 'jobs');
    const archiveQuery = query(jobsRef, where('status', 'in', ['completed', 'ready_to_install', 'scheduled', 'installed']));

    const unsubscribe = onSnapshot(archiveQuery, (snapshot) => {
      const jobs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(j => j.cutlistJobId);

      jobs.sort((a, b) => {
        const dateA = a.updatedAt?.toDate?.() || a.productionStartedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.updatedAt?.toDate?.() || b.productionStartedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setArchiveJobs(jobs);
    }, (error) => {
      console.error('Archive listener error:', error);
    });

    return () => unsubscribe();
  }, []);

  const startProduction = async (jobId, shopWorkerName) => {
    try {
      await startProductionService(jobId, shopWorkerName);
      return { success: true };
    } catch (error) {
      console.error('Error starting production:', error);
      throw error;
    }
  };

  const value = {
    queueJobs,
    activeJobs,
    archiveJobs,
    loading,
    startProduction,
    totalJobs: queueJobs.length + activeJobs.length
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
}